import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../models/content.models';

export type CrmDetailArea = 'platform' | 'tenant';
export type CrmTaskEntityType = 'lead' | 'applicant';
export type CrmTaskStatus = 'NEW' | 'IN_PROGRESS' | 'COMPLETED';
export type CrmTaskReminderStatus = 'pending' | 'sent' | 'cancelled' | 'failed';

export const CRM_TASK_DEFAULT_REMINDER_OFFSET_MINUTES = 10;

export const CRM_TASK_REMINDER_OPTIONS = [
  { value: 10, label: '10 min' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1H' },
  { value: 120, label: '2H' },
] as const;

export type CrmTaskDto = {
  id: string;
  entityType: CrmTaskEntityType;
  entityId: string;
  title: string;
  description?: string;
  assignedTo: string;
  assignedToDisplayName?: string;
  status: CrmTaskStatus;
  statusLabel: string;
  createdBy: string;
  createdByDisplayName?: string;
  dueAt?: string;
  reminderOffsetMinutes?: number;
  reminderAt?: string;
  reminderStatus?: CrmTaskReminderStatus;
  reminderSentAt?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type CreateCrmTaskPayload = {
  title: string;
  description?: string;
  assignedTo: string;
  dueAt?: string;
  reminderOffsetMinutes?: number;
};

export type UpdateCrmTaskPayload = {
  title?: string;
  description?: string;
  assignedTo?: string;
  status?: CrmTaskStatus;
  dueAt?: string | null;
  reminderOffsetMinutes?: number;
};

@Injectable({ providedIn: 'root' })
export class CrmTasksService {
  constructor(private readonly api: ApiService) {}

  list(area: CrmDetailArea, entityType: CrmTaskEntityType, entityId: string) {
    const prefix = this.entityPrefix(area, entityType);
    return this.api
      .get<ApiEnvelope<CrmTaskDto[]>>(`${prefix}/${entityId}/tasks`)
      .pipe(map((r) => r.data));
  }

  create(
    area: CrmDetailArea,
    entityType: CrmTaskEntityType,
    entityId: string,
    payload: CreateCrmTaskPayload,
  ) {
    const prefix = this.entityPrefix(area, entityType);
    return this.api
      .post<ApiEnvelope<CrmTaskDto>>(`${prefix}/${entityId}/tasks`, payload)
      .pipe(map((r) => r.data));
  }

  update(
    area: CrmDetailArea,
    entityType: CrmTaskEntityType,
    entityId: string,
    taskId: string,
    payload: UpdateCrmTaskPayload,
  ) {
    const prefix = this.entityPrefix(area, entityType);
    return this.api
      .patch<ApiEnvelope<CrmTaskDto>>(`${prefix}/${entityId}/tasks/${taskId}`, payload)
      .pipe(map((r) => r.data));
  }

  delete(area: CrmDetailArea, entityType: CrmTaskEntityType, entityId: string, taskId: string) {
    const prefix = this.entityPrefix(area, entityType);
    return this.api
      .delete<ApiEnvelope<{ deleted: boolean }>>(`${prefix}/${entityId}/tasks/${taskId}`)
      .pipe(map((r) => r.data));
  }

  private entityPrefix(area: CrmDetailArea, entityType: CrmTaskEntityType): string {
    const scope = area === 'tenant' ? 'tenant' : 'platform';
    const resource = entityType === 'lead' ? 'leads' : 'applicants';
    return `${scope}/${resource}`;
  }
}
